import {useState, useEffect, useMemo} from 'react'
import DataTable, {
  Column,
} from '../../../../Components/shared/data-table/src/app/Components/shared/data-table/table'
import {
  GridSortModel,
  GridSortItem,
} from '../../../../Components/shared/data-table/src/app/Components/shared/data-table/pagination.types'
import moment from 'moment'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {
  IContact,
  IFlowInstance,
  LOGS_STATUS,
  TriggerEventType,
} from '../../../../Utils/Constant/FlowConstants'
import {Badge, Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {ResumeInstanceModal, PauseInstanceModal, CancelInstanceModal} from './Components'
import {useNavigate, useParams} from 'react-router-dom'
import {
  useCancelInstanceMutation,
  useGetInstanceLogsQuery,
  useGetInstanceLogsStatusQuery,
  usePauseInstanceMutation,
  useResumeInstanceMutation,
  useGetCreditRequiredInstanceQuery,
} from '../../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import PurchasePopup, {getMinimumCredit} from '../../../../Components/PurchasePopup'

interface IFlowInstanceWithId extends IFlowInstance {
  id: string
  index?: number
}

const constructorNameContact = (contact: IContact) => {
  if (!contact?.fields) return contact?.name || contact?.number

  const fullNameField = contact.fields.find((field) => field.name === 'Full Name')
  if (fullNameField) {
    return fullNameField.value
  }

  const firstName = contact.fields.find((field) => field.name === 'First Name')?.value
  const lastName = contact.fields.find((field) => field.name === 'Last Name')?.value

  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ')
  }

  return contact?.name || contact?.number
}

export const renderRowStatus = (status: string) => {
  switch (status) {
    case LOGS_STATUS.RUNNING:
      return 'In Progress'
    case LOGS_STATUS.PAUSED:
      return 'Paused'
    case LOGS_STATUS.CANCELLED:
      return 'Cancelled'
    case LOGS_STATUS.PENDING:
      return 'In Progress'
    case LOGS_STATUS.WAITING:
      return 'In Progress'
    case LOGS_STATUS.COMPLETED:
      return 'Completed'
    case LOGS_STATUS.FAILED:
      return 'Failed'
    default:
      return status
  }
}
const FlowInstance = () => {
  const {t} = useI18n()
  const navigate = useNavigate()
  const {id: flowId} = useParams<{id: string}>()

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [sortData, setSortData] = useState<GridSortModel>({field: 'createdAt', sort: 'desc'})
  const [selectedInstance, setSelectedInstance] = useState<IFlowInstanceWithId | null>(null)
  const [showResumeModal, setShowResumeModal] = useState(false)
  const [showPauseModal, setShowPauseModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [resumeInstanceId, setResumeInstanceId] = useState<string>('')
  const [pauseInstanceId, setPauseInstanceId] = useState<string>('')
  const [cancelInstanceId, setCancelInstanceId] = useState<string>('')
  const [purchasePopupModal, setPurchasePopupModal] = useState(false)
  const [purchaseModelData, setPurchaseModelData] = useState({
    type: 'sms',
    purchaseCredit: 0,
    minimum: 0,
    purpose: 'resume the flow instance',
  })
  const {
    data: instanceLogsResponse,
    isLoading,
    error,
    refetch,
  } = useGetInstanceLogsQuery(
    {
      page: page + 1,
      limit: pageSize,
      id: flowId || '',
      field: sortData.field,
      sort: sortData.sort === 'asc' ? 'asc' : 'desc',
    },
    {
      skip: !flowId,
      refetchOnMountOrArgChange: true,
    }
  )

  const {
    data: instanceLogsStatusResponse,
    isLoading: isLoadingStatus,
    error: errorStatus,
    refetch: refetchStatus,
  } = useGetInstanceLogsStatusQuery(
    {id: flowId || ''},
    {
      skip: !flowId,
      refetchOnMountOrArgChange: true,
    }
  )

  const [pauseInstance, {isLoading: isPausing}] = usePauseInstanceMutation()
  const [resumeInstance, {isLoading: isResuming}] = useResumeInstanceMutation()
  const [cancelInstance, {isLoading: isCancelling}] = useCancelInstanceMutation()
  const {data: creditData, refetch: refetchCredits} = useGetCreditRequiredInstanceQuery(
    {
      id: selectedInstance?._id || '',
    },
    {
      skip: !selectedInstance?._id,
      refetchOnMountOrArgChange: true,
    }
  )
  const flowStatistics = instanceLogsStatusResponse?.data
  const statistics = flowStatistics?.statistics
  const statusBreakdown = statistics?.statusBreakdown
  useEffect(() => {
    if (error) {
      toast.error(t('FLOW.LOGS.FETCH_ERROR') || 'Failed to fetch instance logs')
    }
  }, [error, t])

  useEffect(() => {
    if (isPausing || isResuming) {
      refetch()
    }
  }, [isPausing, isResuming, refetch])

  useEffect(() => {
    if (errorStatus) {
      toast.error(t('FLOW.LOGS.FETCH_ERROR') || 'Failed to fetch instance logs status')
    }
  }, [errorStatus, t])

  const instances = useMemo(() => instanceLogsResponse?.data?.items ?? [], [instanceLogsResponse])

  useEffect(() => {
    if (!isLoading && instances.length === 0 && page > 0) {
      setPage(0)
    }
  }, [instances, isLoading, page])

  const totalData = instanceLogsResponse?.data?.total ?? 0

  const flowName = useMemo(() => {
    return flowStatistics?.flowName
  }, [flowStatistics])

  const instanceRows: IFlowInstanceWithId[] = instances.map((inst: IFlowInstance, idx: number) => ({
    ...inst,
    id: idx.toString(),
    index: idx + 1,
  }))

  const statusBadgeClass = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case LOGS_STATUS.COMPLETED:
        return 'badge badge-light-success fw-bold'
      case LOGS_STATUS.PENDING:
      case LOGS_STATUS.WAITING:
        return 'badge badge-light-warning fw-bold'
      case LOGS_STATUS.RUNNING:
        return 'badge badge-light-info fw-bold'
      case LOGS_STATUS.FAILED:
      case LOGS_STATUS.PAUSED:
      case LOGS_STATUS.CANCELLED:
        return 'badge badge-light-danger fw-bold'
      case TriggerEventType.ADDED_CONTACT:
        return 'badge badge-light-primary fw-bold'
      case TriggerEventType.ADDED_SEGMENT:
        return 'badge badge-light-primary fw-bold'
      case TriggerEventType.ADDED_TAG:
        return 'badge badge-light-primary fw-bold'

      default:
        return 'badge badge-light-secondary fw-bold'
    }
  }

  const renderTriggerEvent = (type: string) => {
    switch (type) {
      case TriggerEventType.ADDED_CONTACT:
        return 'New Contact Added'
      case TriggerEventType.ADDED_SEGMENT:
        return 'Contact added to segment  '
      case TriggerEventType.ADDED_TAG:
        return 'Tag added to contact'
      default:
        return type
    }
  }

  const instanceColumns: Column<IFlowInstanceWithId>[] = [
    {
      id: 'index',
      label: 'Index',
      width: 120,
      Cell: (row) => <span>{row?.index}</span>,
    },
    {
      id: 'contactName',
      label: t('FLOW.LOGS.CONTACT_NAME'),
      width: 120,
      Cell: (row) => (
        <span>
          {row.triggerEvent?.contact?._id
            ? constructorNameContact({
                ...row.triggerEvent.contact,
                _id: row.triggerEvent.contact._id,
              } as IContact)
            : ''}
        </span>
      ),
    },
    {
      id: 'contactNumber',
      label: t('FLOW.LOGS.CONTACT_NUMBER'),
      width: 120,
      Cell: (row) => <span>{row.triggerEvent?.contact?.number}</span>,
    },
    {
      id: 'status',
      label: t('FLOW.LOGS.STATUS'),
      width: 100,
      Cell: (row) => (
        <Badge bg={statusBadgeClass(row.status)} style={{fontSize: 13}}>
          {renderRowStatus(row.status)}
        </Badge>
      ),
    },
    {
      id: 'triggerEvent',
      label: t('FLOW.LOGS.TRIGGER'),
      width: 120,
      Cell: (row) => (
        <Badge bg={statusBadgeClass(row.triggerEvent?.type)} style={{fontSize: 13}}>
          {renderTriggerEvent(row.triggerEvent?.type)}
        </Badge>
      ),
    },
    {
      id: 'createdAt',
      label: t('FLOW.LOGS.CREATED_AT'),
      width: 170,
      sortable: true,
      Cell: (row) => (
        <span>
          {row.createdAt ? moment(row.createdAt).local().format('MMM DD YYYY HH:mm:ss') : '-'}
        </span>
      ),
    },
    {
      id: 'updatedAt',
      label: t('FLOW.LOGS.UPDATED_AT'),
      width: 170,
      sortable: true,
      Cell: (row) => (
        <span>
          {row.updatedAt ? moment(row.updatedAt).local().format('MMM DD YYYY HH:mm:ss') : '-'}
        </span>
      ),
    },
    {
      id: 'actions',
      label: t('FLOW.LOGS.ACTIONS'),
      width: 80,
      Cell: (row: IFlowInstanceWithId) => (
        <div className='d-flex gap-2 p-2'>
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip>{t('FLOW.LOGS.VIEW_INSTANCE_DESC')}</Tooltip>}
          >
            <button
              className='btn btn-sm btn-icon btn-light btn-active-light-info'
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/automation/contacts-in-flows/event-logs/${row._id}`)
              }}
              title={t('FLOW.LOGS.VIEW_INSTANCE')}
            >
              <i className='bi bi-eye'></i>
            </button>
          </OverlayTrigger>
          {(row.status === LOGS_STATUS.RUNNING ||
            row.status === LOGS_STATUS.WAITING ||
            row.status === LOGS_STATUS.PENDING) && (
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip>{t('FLOW.INSTANCE.PAUSE_JOB_DESC')}</Tooltip>}
            >
              <button
                className='btn btn-sm btn-icon btn-warning'
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedInstance(row)
                  handlePauseClick(row._id)
                }}
                title={t('FLOW.INSTANCE.PAUSE_INSTANCE')}
                disabled={isPausing && selectedInstance?._id === row._id}
              >
                {isPausing && selectedInstance?._id === row._id ? (
                  <>
                    <Spinner animation='border' size='sm' />
                  </>
                ) : (
                  <i className='bi bi-pause'></i>
                )}
              </button>
            </OverlayTrigger>
          )}
          {row.status === LOGS_STATUS.PAUSED && (
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip>{t('FLOW.INSTANCE.RESUME_JOB_DESC')}</Tooltip>}
            >
              <button
                className='btn btn-sm btn-icon btn-success'
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedInstance(row)
                  handleResumeClick(row._id)
                }}
                title={t('FLOW.INSTANCE.RESUME_INSTANCE')}
                disabled={isResuming && selectedInstance?._id === row._id}
              >
                {isResuming && selectedInstance?._id === row._id ? (
                  <>
                    <Spinner animation='border' size='sm' />
                  </>
                ) : (
                  <i className='bi bi-play'></i>
                )}
              </button>
            </OverlayTrigger>
          )}
          {(row.status === LOGS_STATUS.RUNNING ||
            row.status === LOGS_STATUS.WAITING ||
            row.status === LOGS_STATUS.PENDING ||
            row.status === LOGS_STATUS.PAUSED) && (
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip>{t('FLOW.INSTANCE.CANCEL_INSTANCE_DESC')}</Tooltip>}
            >
              <button
                className='btn btn-sm btn-icon btn-danger'
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedInstance(row)
                  handleCancelClick(row._id)
                }}
                title={t('FLOW.INSTANCE.CANCEL_INSTANCE')}
                disabled={selectedInstance?._id === row._id && isCancelling}
              >
                {isCancelling && selectedInstance?._id === row._id ? (
                  <Spinner animation='border' size='sm' />
                ) : (
                  <i className='bi bi-x-circle'></i>
                )}
              </button>
            </OverlayTrigger>
          )}
        </div>
      ),
    },
  ]

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0)
  }

  const handleSortChange = (sortData: GridSortItem) => {
    setSortData({field: sortData.field, sort: sortData.sort ?? 'desc'})
  }

  const handlePauseInstance = (id: string) => {
    pauseInstance({id}).then((res) => {
      if ('data' in res && res.data) {
        toast.success(t('FLOW.INSTANCE.PAUSE_INSTANCE_SUCCESS'))
        refetch()
        refetchStatus()
        setSelectedInstance(null)
        setShowPauseModal(false)
      } else if ('error' in res && res.error) {
        toast.error(t('FLOW.INSTANCE.PAUSE_INSTANCE_ERROR'))
      }
    })
  }

  const handlePauseClick = (id: string) => {
    setPauseInstanceId(id)
    setShowPauseModal(true)
  }

  const handlePauseConfirm = () => {
    if (pauseInstanceId) {
      handlePauseInstance(pauseInstanceId)
    }
  }

  const handleResumeInstance = async (id: string) => {
    if (creditData?.data) {
      const {requiredCredits, currentCredits} = creditData.data
      if (currentCredits < requiredCredits) {
        console.log('🚀 Insufficient credits:', {requiredCredits, currentCredits})
        setPurchaseModelData({
          type: 'sms',
          purchaseCredit: getMinimumCredit(requiredCredits),
          minimum: requiredCredits,
          purpose: 'resume the flow instance (SMS required)',
        })
        setPurchasePopupModal(true)
        setShowResumeModal(true)
        return
      }
    }

    resumeInstance({id}).then((res) => {
      if ('data' in res && res.data) {
        toast.success(res.data.message)
        refetch()
        refetchStatus()
        setSelectedInstance(null)
        setShowResumeModal(false)
      } else if ('error' in res && res.error) {
        toast.error(t('FLOW.INSTANCE.RESUME_INSTANCE_ERROR'))
      }
    })
  }

  const handleResumeClick = (id: string) => {
    setResumeInstanceId(id)
    setShowResumeModal(true)
  }

  const handleResumeConfirm = () => {
    if (resumeInstanceId) {
      handleResumeInstance(resumeInstanceId)
    }
  }

  const handleCancelInstance = (id: string) => {
    cancelInstance({id}).then((res) => {
      if ('data' in res && res.data) {
        toast.success(res.data.message)
        refetch()
        refetchStatus()
        setSelectedInstance(null)
        setShowCancelModal(false)
      } else if ('error' in res && res.error) {
        toast.error(t('FLOW.INSTANCE.CANCEL_INSTANCE_ERROR'))
      }
    })
  }

  const handleCancelClick = (id: string) => {
    setCancelInstanceId(id)
    setShowCancelModal(true)
  }

  const handleCancelConfirm = () => {
    if (cancelInstanceId) {
      handleCancelInstance(cancelInstanceId)
    }
  }

  if (!flowId) {
    return (
      <div className='card'>
        <div className='card-body'>
          <div className='alert alert-warning'>
            {t('FLOW.LOGS.NO_FLOW_ID') || 'No flow ID provided'}
          </div>
        </div>

        <PurchasePopup
          show={purchasePopupModal}
          purchaseModelData={purchaseModelData}
          handleClose={() => {
            setPurchasePopupModal(false)
            // Refetch credits after purchase to get updated balance
            refetchCredits()
          }}
          purpose={purchaseModelData.purpose}
        />
        <ResumeInstanceModal
          show={showResumeModal}
          onHide={() => setShowResumeModal(false)}
          onConfirm={handleResumeConfirm}
          isResuming={isResuming}
        />

        <PauseInstanceModal
          show={showPauseModal}
          onHide={() => setShowPauseModal(false)}
          onConfirm={handlePauseConfirm}
          isPausing={isPausing}
        />

        <CancelInstanceModal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          onConfirm={handleCancelConfirm}
          isCancelling={isCancelling}
        />
      </div>
    )
  }

  const navigateBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/automation/logs')
    }
  }
  return (
    <div className='card'>
      <div className='shadow-sm'>
        <div className='card-header border-0 p-0 align-items-center'>
          <div className='d-flex flex-column w-100'>
            <h3 className='card-title align-items-start flex-column'>
              <div className='d-flex align-items-center gap-3 mb-2'>
                <OverlayTrigger
                  placement='right'
                  overlay={<Tooltip>Go back to automation flows</Tooltip>}
                >
                  <span
                    className='me-2'
                    style={{cursor: 'pointer'}}
                    onClick={() => navigateBack()}
                    title={t('COMMON.ACTIONS.BACK')}
                  >
                    <i className='bi bi-arrow-left fs-4 text-primary'></i>
                  </span>
                </OverlayTrigger>
                <span className='card-label fw-bolder fs-2 mb-1'>{t('FLOW.INSTANCE.TITLE')}</span>
              </div>
              <span className=' mt-1 fw-bold fs-7'>
                {t('FLOW.INSTANCE.SUBTITLE', {flowName: flowName || ''})}
              </span>
            </h3>
          </div>
        </div>

        {isLoadingStatus ? (
          <div className='d-flex justify-content-between align-items-center ms-6'>
            <div
              className='d-flex flex-column gap-1 mt-2'
              style={{
                lineHeight: 1.2,
                width: 180,
              }}
            >
              <div className='fw-bold' style={{fontSize: 14}}>
                <div className='placeholder-glow'>
                  <span
                    className='placeholder col-8'
                    style={{height: 18, display: 'inline-block'}}
                  ></span>
                </div>
              </div>
              <div className='fw-bold' style={{fontSize: 14}}>
                <div className='placeholder-glow'>
                  <span
                    className='placeholder col-8'
                    style={{height: 18, display: 'inline-block'}}
                  ></span>
                </div>
              </div>
            </div>
            <div className='row g-2' style={{width: 700, maxWidth: '100%'}}>
              {[...Array(7)].map((_, idx) => (
                <div className='col-3' key={idx}>
                  <div className='text-center'>
                    <div className='placeholder-glow'>
                      <span
                        className='placeholder rounded-circle'
                        style={{width: 24, height: 24, display: 'inline-block', marginBottom: 4}}
                      ></span>
                    </div>
                    <div className='placeholder-glow'>
                      <span
                        className='placeholder col-6'
                        style={{height: 16, display: 'inline-block', marginBottom: 2}}
                      ></span>
                    </div>
                    <div className='placeholder-glow'>
                      <span
                        className='placeholder col-7'
                        style={{height: 10, display: 'inline-block'}}
                      ></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center ms-2 ms-md-6 gap-2 gap-md-0'>
            <div
              className='d-flex flex-row gap-2 mt-2 w-100 align-items-start'
              style={{
                lineHeight: 1.2,
              }}
            >
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>{t('FLOW.INSTANCE.TOTAL_CUSTOMERS_DESC')}</Tooltip>}
              >
                <span className='fw-bold' style={{fontSize: 16}}>
                  {t('FLOW.INSTANCE.TOTAL_CUSTOMERS', {
                    totalCustomers: statistics?.totalCustomers || 0,
                  })}
                </span>
              </OverlayTrigger>

              <span className='fw-bold text-muted' style={{fontSize: 16}}>
                |
              </span>

              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>{t('FLOW.INSTANCE.TOTAL_CUSTOMERS_DESC')}</Tooltip>}
              >
                <span className='fw-bold' style={{fontSize: 16}}>
                  {t('FLOW.INSTANCE.TOTAL_INSTANCES', {
                    totalInstances: statistics?.totalInstances || 0,
                  })}
                </span>
              </OverlayTrigger>

              <span className='fw-bold text-muted' style={{fontSize: 16}}>
                |
              </span>

              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>Total number of bites (contact replies)</Tooltip>}
              >
                <span className='fw-bold' style={{fontSize: 16}}>
                  {t('FLOW.INSTANCE.TOTAL_BITES', {
                    totalBites: statistics?.totalBites || 0,
                  })}
                </span>
              </OverlayTrigger>
            </div>

            <div
              className='d-flex flex-row flex-wrap align-items-center
              justify-content-md-end justify-content-sm-start gap-2 w-100'
              style={{minWidth: 0}}
            >
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>Flow instances currently in progress</Tooltip>}
              >
                <div className='text-center' style={{minWidth: 80}}>
                  <i
                    className='bi bi-play-circle text-primary'
                    style={{fontSize: 24, verticalAlign: 'middle'}}
                  ></i>
                  <span className='fw-bold text-primary' style={{fontSize: 13, marginLeft: 3}}>
                    {statusBreakdown?.running || 0}
                  </span>
                  <div style={{fontSize: 12}}>{t('FLOW.INSTANCE.STATUS_RUNNING')}</div>
                </div>
              </OverlayTrigger>
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>Flow instances that have been paused</Tooltip>}
              >
                <div className='text-center' style={{minWidth: 80}}>
                  <i
                    className='bi bi-pause-circle text-muted'
                    style={{fontSize: 24, verticalAlign: 'middle'}}
                  ></i>
                  <span className='fw-bold text-muted' style={{fontSize: 13, marginLeft: 3}}>
                    {statusBreakdown?.paused || 0}
                  </span>
                  <div style={{fontSize: 12}}>{t('FLOW.INSTANCE.STATUS_PAUSED')}</div>
                </div>
              </OverlayTrigger>
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>Flow instances that encountered errors</Tooltip>}
              >
                <div className='text-center' style={{minWidth: 80}}>
                  <i
                    className='bi bi-x-circle text-danger'
                    style={{fontSize: 24, verticalAlign: 'middle'}}
                  ></i>
                  <span className='fw-bold text-muted' style={{fontSize: 13, marginLeft: 3}}>
                    {statistics?.failedCustomers || 0}
                  </span>
                  <div style={{fontSize: 12}}>{t('FLOW.INSTANCE.STATUS_FAILED')}</div>
                </div>
              </OverlayTrigger>

              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>Flow instances that were cancelled</Tooltip>}
              >
                <div className='text-center' style={{minWidth: 80}}>
                  <i
                    className='bi bi-x-circle text-danger'
                    style={{fontSize: 24, verticalAlign: 'middle'}}
                  ></i>
                  <span className='fw-bold text-muted' style={{fontSize: 13, marginLeft: 3}}>
                    {statusBreakdown?.cancelled || 0}
                  </span>
                  <div style={{fontSize: 12}}>{t('FLOW.INSTANCE.STATUS_CANCELLED')}</div>
                </div>
              </OverlayTrigger>
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>Flow instances that have finished successfully</Tooltip>}
              >
                <div className='text-center' style={{minWidth: 80}}>
                  <i
                    className='bi bi-check-circle text-success'
                    style={{fontSize: 24, verticalAlign: 'middle'}}
                  ></i>
                  <span className='fw-bold text-success' style={{fontSize: 13, marginLeft: 3}}>
                    {statusBreakdown?.completed || 0}
                  </span>
                  <div style={{fontSize: 12}}>{t('FLOW.INSTANCE.STATUS_COMPLETED')}</div>
                </div>
              </OverlayTrigger>
            </div>
          </div>
        )}

        <div className='card-body p-0 pt-1'>
          <DataTable
            items={instanceRows}
            isLoading={isLoading}
            columns={instanceColumns}
            sortData={sortData}
            handleSortedChange={handleSortChange}
            pageSize={pageSize}
            totalData={totalData}
            handleChangePage={handlePageChange}
            handleChangSizeData={handlePageSizeChange}
            page={page}
            onRowClick={(item) => navigate(`/automation/contacts-in-flows/event-logs/${item._id}`)}
            onRowHover={(item, isHovering) => {}}
          />
        </div>
      </div>

      {/* Modal Components */}
      <PurchasePopup
        show={purchasePopupModal}
        purchaseModelData={purchaseModelData}
        handleClose={() => {
          setPurchasePopupModal(false)
          // Refetch credits after purchase to get updated balance
          refetchCredits()
        }}
        purpose={purchaseModelData.purpose}
      />
      <ResumeInstanceModal
        show={showResumeModal}
        onHide={() => setShowResumeModal(false)}
        onConfirm={handleResumeConfirm}
        isResuming={isResuming}
      />

      <PauseInstanceModal
        show={showPauseModal}
        onHide={() => setShowPauseModal(false)}
        onConfirm={handlePauseConfirm}
        isPausing={isPausing}
      />

      <CancelInstanceModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        isCancelling={isCancelling}
      />
    </div>
  )
}

export default FlowInstance
